'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMatch, useMatchFantasyPlayers, useFormations, useCreateFantasyTeam, useUpdateFantasyTeam, useMyTeamForMatch, useSubmitFantasyTeam } from '../hooks/useFootball'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import { showToast } from '../components/ui/Toast'
import { useT } from '../contexts/LocaleContext'

import {
  PITCH_LAYOUTS,
  POSITION_ICONS,
  POSITION_ORDER,
  assignByPosition,
  buildSlots,
  getPositionLabel,
  toCard,
  type Card_,
  type PitchSlot,
} from '../components/football/pitchLayouts'
import FootballPitchShell from '../components/football/FootballPitchShell'
import PitchPlayerToken from '../components/football/PitchPlayerToken'

export default function FantasyTeamBuilderPage({ matchId }: { matchId: number }) {
  const navigate = useNavigate()
  const t = useT()
  const { data: matchData } = useMatch(matchId)
  const { data: poolData, isLoading: playersLoading } = useMatchFantasyPlayers(matchId)
  const { data: formationsData } = useFormations()
  const { data: myTeamData } = useMyTeamForMatch(matchId)
  const createTeam = useCreateFantasyTeam()
  const updateTeam = useUpdateFantasyTeam()
  const submitTeam = useSubmitFantasyTeam()

  const [step, setStep] = useState<1 | 2>(1)
  const [teamName, setTeamName] = useState('')
  const [formation, setFormation] = useState('4-4-2')
  const [lineup, setLineup] = useState<Record<string, Card_>>({})
  const [activeSlotKey, setActiveSlotKey] = useState<string | null>(null)
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null)
  const hydrated = useRef(false)
  const cardsRef = useRef<HTMLDivElement>(null)

  const match = matchData?.match
  const pool = poolData?.pool
  const allPlayers = pool?.players || []
  const matchGroups = pool?.groups || []
  const formations = formationsData?.formations || Object.keys(PITCH_LAYOUTS)

  const savedTeam = myTeamData?.team || null
  const isSubmitted = !!savedTeam?.is_submitted
  const isLocked = isSubmitted

  const slots = useMemo(() => buildSlots(formation), [formation])
  const pitchRows = useMemo(() => PITCH_LAYOUTS[formation] || PITCH_LAYOUTS['4-4-2'], [formation])
  const slotAt = useMemo(() => {
    const map: Record<string, PitchSlot> = {}
    slots.forEach((slot) => {
      map[`${slot.row}-${slot.col}`] = slot
    })
    return map
  }, [slots])

  const totalSlots = slots.length
  const selectedCount = slots.filter((s) => lineup[s.key]).length
  const isComplete = selectedCount === totalSlots
  const canSave = teamName.trim().length > 0 && isComplete && !isLocked

  const activeSlot = activeSlotKey ? slots.find((s) => s.key === activeSlotKey) || null : null
  const candidates = useMemo(() => {
    if (!activeSlot) return []
    return allPlayers
      .filter((p) => p.position === activeSlot.position)
      .filter((p) => activeGroupId === null || p.group_ids.includes(activeGroupId))
  }, [allPlayers, activeSlot, activeGroupId])

  const savedLineup = useMemo<Record<string, Card_>>(() => {
    if (!savedTeam?.players) return {}
    const ordered = [...savedTeam.players]
      .sort((a, b) => a.slot_number - b.slot_number)
      .map((tp) => (tp.player ? toCard(tp.player) : null))
      .filter((c): c is Card_ => !!c)
    return assignByPosition(slots, ordered)
  }, [savedTeam, slots])

  const isDirty = useMemo(() => {
    if (!savedTeam) return isComplete
    if (savedTeam.name !== teamName.trim() || savedTeam.formation !== formation) return true
    return JSON.stringify(Object.keys(savedLineup).sort().map((k) => savedLineup[k].id)) !==
      JSON.stringify(Object.keys(lineup).filter((k) => lineup[k]).sort().map((k) => lineup[k].id))
  }, [savedTeam, savedLineup, lineup, teamName, formation, isComplete])

  // Load the saved team once, then jump straight to the pitch
  useEffect(() => {
    if (hydrated.current) return
    hydrated.current = true
    if (!savedTeam) return
    setTeamName(savedTeam.name)
    setFormation(savedTeam.formation || '4-4-2')
    setLineup(savedLineup)
    setStep(2)
    setActiveSlotKey(Object.keys(savedLineup)[0] || 'gk-0')
  }, [savedTeam, savedLineup])

  const focusSlot = useCallback((key: string) => {
    setActiveSlotKey(key)
    setTimeout(() => {
      cardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 60)
  }, [])

  const handleFormationChange = (next: string) => {
    setFormation(next)
    const nextSlots = buildSlots(next)
    const kept = Object.values(lineup)
    setLineup(assignByPosition(nextSlots, kept))
    const stillValid = activeSlotKey && nextSlots.some((s) => s.key === activeSlotKey)
    if (!stillValid) {
      setActiveSlotKey(nextSlots[nextSlots.length - 1]?.key || null)
    }
  }

  const handlePickCard = (option: Card_) => {
    if (!activeSlotKey || isLocked) return
    const currentIndex = slots.findIndex((s) => s.key === activeSlotKey)
    setLineup((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((key) => {
        if (next[key].id === option.id) delete next[key]
      })
      next[activeSlotKey] = option
      return next
    })

    const isFilled = (key: string) => key === activeSlotKey || !!lineup[key]
    const nextEmpty = [...slots.slice(currentIndex + 1), ...slots.slice(0, currentIndex)].find((s) => !isFilled(s.key))
    if (nextEmpty) {
      setActiveSlotKey(nextEmpty.key)
    }
  }

  const handleRemoveFromSlot = (key: string) => {
    if (isLocked) return
    setLineup((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    focusSlot(key)
  }

  const handleGoToStep2 = () => {
    if (!teamName.trim()) {
      showToast(t('fantasy.builder.toastEnterName'), 'error')
      return
    }
    setStep(2)
    const firstEmpty = slots.find((s) => !lineup[s.key]) || slots[slots.length - 1]
    setActiveSlotKey(firstEmpty.key)
  }

  const buildPayload = () => ({
    name: teamName.trim(),
    formation,
    match_id: matchId,
    players: slots
      .filter((s) => lineup[s.key])
      .map((slot, index) => ({
        player_id: lineup[slot.key].id,
        position: lineup[slot.key].position,
        slot: index + 1,
      })),
  })

  const handleSave = async () => {
    if (!canSave) {
      showToast(
        isComplete
          ? t('fantasy.builder.toastEnterName')
          : t('fantasy.builder.toastPickAllSlots', { totalSlots }),
        'error',
      )
      return
    }
    try {
      const payload = buildPayload()
      if (savedTeam) {
        await updateTeam.mutateAsync({ teamId: savedTeam.id, ...payload })
        showToast(t('fantasy.builder.toastLineupUpdated'), 'success')
      } else {
        await createTeam.mutateAsync(payload)
        showToast(t('fantasy.builder.toastTeamCreated'), 'success')
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || t('fantasy.builder.toastSaveError'), 'error')
    }
  }

  const handleSubmit = async () => {
    if (!savedTeam) return
    try {
      await submitTeam.mutateAsync(savedTeam.id)
      showToast(t('fantasy.builder.toastSubmittedForPoll'), 'success')
    } catch (err: any) {
      showToast(err.response?.data?.message || t('fantasy.builder.toastSubmitError'), 'error')
    }
  }

  const shareUrl = savedTeam?.share_token ? `${window.location.origin}/fantasy/vote?token=${savedTeam.share_token}` : ''

  const handleCopyLink = async () => {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      showToast(t('fantasy.builder.toastLinkCopied'), 'success')
    } catch {
      showToast(t('fantasy.builder.toastCopyFailed'), 'error')
    }
  }

  const handleShare = async () => {
    if (!shareUrl) return
    const text = t('fantasy.builder.shareVoteText', { teamName: savedTeam?.name || '' })
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`

    if (navigator.share) {
      try {
        await navigator.share({ title: text, url: shareUrl })
        return
      } catch {
        // fall through to opening the Telegram share dialog
      }
    }
    window.open(telegramUrl, '_blank')
  }

  if (playersLoading) {
    return (
      <div className="lucky-fb mini-app-container pb-20 min-h-screen flex items-center justify-center">
        <div className="lucky-fb__empty">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="lucky-fb mini-app-container pb-32 min-h-screen">
      <div className="lucky-fb__inner">
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => (step === 2 && !savedTeam ? setStep(1) : navigate('/football'))}
            className="lucky-wallet__back"
            aria-label={t('common.back')}
          >
            →
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">{t('fantasy.builder.title')}</h1>
            {match && (
              <p className="text-xs text-[#9eb6e0]">
                {match.home_team} vs {match.away_team}
              </p>
            )}
          </div>
          <div className="text-xs rounded-full px-3 py-1 border border-[rgba(90,140,220,0.32)] bg-[rgba(12,28,58,0.78)]">
            {t('fantasy.builder.stepOf', { step })}
          </div>
        </div>

        {step === 1 ? (
          <>
            <Card className="p-4 mb-4">
              <h3 className="font-bold mb-3">{t('fantasy.builder.stepTeamName')}</h3>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                maxLength={40}
                placeholder={t('dreamTeam.builder.placeholderTeamName')}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-primary focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-2">{t('fantasy.builder.charCount', { length: teamName.trim().length })}</p>
            </Card>

            <Card className="p-4 mb-4">
              <h3 className="font-bold mb-3">{t('fantasy.builder.stepFormation')}</h3>
              <div className="grid grid-cols-3 gap-2">
                {formations.map((f) => {
                  const rows = PITCH_LAYOUTS[f] || []
                  const counts = POSITION_ORDER.map((pos) => ({
                    pos,
                    count: rows.filter((r) => r.position === pos).reduce((sum, r) => sum + r.cols.length, 0),
                  })).filter((c) => c.count > 0)
                  return (
                    <button
                      key={f}
                      onClick={() => handleFormationChange(f)}
                      className={`rounded-xl border p-2 text-center transition ${
                        formation === f
                          ? 'border-primary bg-primary/15'
                          : 'border-gray-700 bg-gray-800/40 hover:border-gray-600'
                      }`}
                    >
                      <div className="text-sm font-bold">{f}</div>
                      <div className="flex justify-center gap-1 mt-1">
                        {counts.map((c) => (
                          <span key={c.pos} className="text-[10px] text-gray-400">
                            {c.count}
                            {POSITION_ICONS[c.pos]}
                          </span>
                        ))}
                      </div>
                    </button>
                  )
                })}
              </div>
            </Card>

            {matchGroups.length > 0 && (
              <Card className="p-4 mb-4">
                <h3 className="font-bold mb-2">{t('fantasy.builder.stepCardGroups')}</h3>
                <div className="flex flex-wrap gap-2">
                  {matchGroups.map((group) => (
                    <span key={group.id} className="text-xs bg-gray-800 rounded-full px-3 py-1 text-gray-300">
                      {group.title}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('fantasy.builder.cardGroupsHint')}
                </p>
              </Card>
            )}

            <Button className="w-full" onClick={handleGoToStep2} disabled={!teamName.trim()}>
              {t('fantasy.builder.nextStep')}
            </Button>
          </>
        ) : (
          <>
            {/* Progress */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">
                {t('dreamTeam.builder.playersSelected', { selected: selectedCount, total: totalSlots })}
              </span>
              {isLocked && <span className="text-xs text-green-400">{t('dreamTeam.status.teamSubmittedCheck')}</span>}
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all"
                style={{ width: `${(selectedCount / totalSlots) * 100}%` }}
              />
            </div>

            <FootballPitchShell
              className="mb-4"
              boardText={
                teamName.trim()
                  ? `${teamName.trim()} · ${match?.home_team || ''} vs ${match?.away_team || ''}`
                  : `${match?.home_team || ''} vs ${match?.away_team || ''}`
              }
            >
              {pitchRows.map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-5 gap-1 items-center">
                  {[0, 1, 2, 3, 4].map((col) => {
                    const slot = slotAt[`${rowIndex}-${col}`]
                    if (!slot) return <div key={col} />
                    const player = lineup[slot.key]
                    return (
                      <PitchPlayerToken
                        key={col}
                        player={player}
                        position={slot.position}
                        isActive={activeSlotKey === slot.key && !isLocked}
                        emptyHint={isLocked ? undefined : t('dreamTeam.builder.pickEmpty')}
                        floatKey={rowIndex * 5 + col}
                        locked={isLocked}
                        onClick={() => focusSlot(slot.key)}
                        onRemove={isLocked ? undefined : () => handleRemoveFromSlot(slot.key)}
                      />
                    )
                  })}
                </div>
              ))}
            </FootballPitchShell>

            {/* Group filter */}
            {matchGroups.length > 0 && !isLocked && (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-2">
                <button
                  onClick={() => setActiveGroupId(null)}
                  className={`shrink-0 text-xs rounded-full px-3 py-1.5 border ${
                    activeGroupId === null ? 'border-primary bg-primary/15 text-white' : 'border-gray-700 text-gray-400'
                  }`}
                >
                  {t('dreamTeam.builder.filterAll', { count: allPlayers.length })}
                </button>
                {matchGroups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => setActiveGroupId(group.id)}
                    className={`shrink-0 text-xs rounded-full px-3 py-1.5 border ${
                      activeGroupId === group.id ? 'border-primary bg-primary/15 text-white' : 'border-gray-700 text-gray-400'
                    }`}
                  >
                    {group.title} ({allPlayers.filter((p) => p.group_ids.includes(group.id)).length})
                  </button>
                ))}
              </div>
            )}

            {/* Player cards for the active slot */}
            {!isLocked && (
              <Card className="p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-sm">
                    {activeSlot
                      ? t('dreamTeam.builder.pickPositionTitle', {
                          icon: POSITION_ICONS[activeSlot.position],
                          position: getPositionLabel(activeSlot.position, t),
                        })
                      : t('fantasy.builder.pickSlotHint')}
                  </h3>
                  <span className="text-xs text-gray-400">{t('dreamTeam.builder.cardCount', { count: candidates.length })}</span>
                </div>

                {activeSlot ? (
                  candidates.length === 0 ? (
                    <p className="text-center text-sm text-gray-400 py-6">
                      {t('fantasy.builder.noCardsForPosition')}
                    </p>
                  ) : (
                    <div ref={cardsRef} className="flex gap-2 overflow-x-auto pb-2">
                      {candidates.map((option) => {
                        const inThisSlot = activeSlotKey ? lineup[activeSlotKey]?.id === option.id : false
                        const elsewhere = slots.some((s) => s.key !== activeSlotKey && lineup[s.key]?.id === option.id)
                        return (
                          <button
                            key={option.id}
                            onClick={() => handlePickCard(option)}
                            className={`shrink-0 w-20 rounded-xl overflow-hidden border text-right transition active:scale-95 ${
                              inThisSlot ? 'border-primary ring-2 ring-primary/50' : 'border-gray-700'
                            }`}
                          >
                            <div className="relative">
                              <img
                                src={option.photo_url || '/placeholder.png'}
                                alt={option.name}
                                className="w-20 h-24 object-cover object-top"
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder.png'
                                }}
                              />
                              {inThisSlot && (
                                <span className="absolute top-1 left-1 bg-primary text-[9px] rounded-full px-1.5 py-0.5">
                                  {t('dreamTeam.builder.badgeSelected')}
                                </span>
                              )}
                              {elsewhere && !inThisSlot && (
                                <span className="absolute top-1 left-1 bg-yellow-500 text-gray-900 text-[9px] rounded-full px-1.5 py-0.5">
                                  {t('dreamTeam.builder.badgeInTeam')}
                                </span>
                              )}
                            </div>
                            <div className="px-1 py-1 bg-gray-800/80">
                              <div className="text-[10px] truncate">{option.name}</div>
                              <div className="text-[9px] text-gray-400 truncate">
                                {option.group_titles[0] || option.team}
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )
                ) : (
                  <p className="text-center text-sm text-gray-400 py-6">{t('dreamTeam.builder.tapEmptySlot')}</p>
                )}
              </Card>
            )}

            {/* Saved lineup summary */}
            {isLocked && (
              <Card className="p-4 mb-4">
                <h3 className="font-bold mb-2">{t('fantasy.builder.registeredLineup')}</h3>
                <div className="space-y-1 text-xs text-gray-300">
                  {POSITION_ORDER.map((pos) => {
                    const names = slots
                      .filter((s) => s.position === pos)
                      .map((s) => lineup[s.key]?.name)
                      .filter(Boolean)
                    return (
                      <div key={pos} className="flex gap-2">
                        <span className="text-gray-500 w-16 shrink-0">{getPositionLabel(pos, t)}</span>
                        <span>{names.join(t('dreamTeam.listSeparator'))}</span>
                      </div>
                    )
                  })}
                </div>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Sticky actions */}
      {step === 2 && (
        <div className="fixed bottom-0 left-0 right-0 z-20 px-4 py-3 bg-gray-900/95 backdrop-blur border-t border-gray-800">
          {isLocked ? (
            <div className="space-y-2">
              <Button variant="secondary" className="w-full" onClick={handleShare}>
                {t('fantasy.builder.shareAndPoll')}
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={handleCopyLink}>
                  {t('fantasy.builder.copyTeamLink')}
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => navigate('/fantasy/explore')}>
                  {t('fantasy.builder.seeOtherTeams')}
                </Button>
              </div>
            </div>
          ) : savedTeam ? (
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={handleSubmit} disabled={submitTeam.isPending}>
                {t('fantasy.builder.submitForPoll')}
              </Button>
              <Button className="flex-1" onClick={handleSave} disabled={!canSave || !isDirty || updateTeam.isPending}>
                {updateTeam.isPending ? t('dreamTeam.builder.saving') : t('dreamTeam.builder.updateTeam')}
              </Button>
            </div>
          ) : (
            <Button className="w-full" onClick={handleSave} disabled={!canSave || createTeam.isPending}>
              {createTeam.isPending
                ? t('dreamTeam.builder.saving')
                : t('fantasy.builder.saveTeam', { selected: selectedCount, total: totalSlots })}
            </Button>
          )}
          {!isLocked && !isComplete && (
            <p className="text-center text-[10px] text-gray-500 mt-2">
              {t('fantasy.builder.saveHint', { totalSlots })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
